import { differenceInCalendarDays, parseISO } from "date-fns";
import {
    allocationPercentsFromAmounts,
    DAILY_PRO_RATA_TERM_UNITS_MESSAGE,
    SINGLE_TERM_ROW_MESSAGE,
    splitAmountByAllocationPercents,
} from "@common/constants/domains/accounting/schedules";
import { currencyFormatter } from "@common/helpers/currency";
import { isSourceDocumentAnchor } from "@common/helpers/domains/accounting/scheduleAnchors";
import { roundToCents } from "@common/helpers/monetary";
import {
    ScheduleRecognitionComponentInput,
    ScheduleRecognitionOverrides,
    ScheduleTemplateResponse,
} from "@common/schemas/domains/accounting/scheduleTemplate.schema";
import {
    RecognitionAnchor,
    isDailyProRataTermUnitAllowed,
    isTermRecognitionMethod,
    RecognitionMethod,
    ScheduleTermUnit,
} from "@common/types/domains/accounting/schedule";
import {
    buildManualRecognitionRows,
    offsetDate,
    resolvePolicyAccountIds,
} from "./scheduleManualApplyHelpers";

/** One recognition row as the modal edits it: an amount and the day it lands. */
export interface ScheduleRowDraft {
    key: string;
    method: RecognitionMethod;
    amount: number;
    /** Days from its anchor; the row's date follows the anchor. */
    offsetDays: number;
    /** The anchor plus the offset, or "" until the anchor is set. */
    date: string;
    /** Anchored on the specific date the user enters, not the term start. */
    isSpecificDate: boolean;
}

export interface ScheduleDraft {
    rows: ScheduleRowDraft[];
    /** The anchors the rows' offsets count from, or "" until set. */
    termStart: string;
    specificDate: string;
    termLength: number;
    termUnit: ScheduleTermUnit;
    holdingAccountId: string | null;
    recognitionAccountId: string | null;
}

export interface ResolvedScheduleDraft {
    amount: number;
    /** Null when every row sits on the specific date. */
    termStart: string | null;
    specificDate: string;
    components: ScheduleRecognitionComponentInput[];
}

type DraftTemplate = Pick<
    ScheduleTemplateResponse,
    "components" | "accountRows" | "termLength" | "termUnit"
>;

export function createScheduleDraft(options: {
    template: DraftTemplate;
    entityId: number | null;
    amount: number;
    termStart: string;
    specificDate: string;
    /** The source's own day, when the schedule is applied to one. */
    sourceDate?: string | null;
    sourceRecognitionAccountId?: string | null;
    term?: { length: number; unit: ScheduleTermUnit };
}): ScheduleDraft {
    const { template } = options;
    const componentsById = new Map(
        template.components.map((component) => [component.id, component]),
    );
    const accountIds = resolvePolicyAccountIds({
        accountRows: template.accountRows,
        entityId: options.entityId,
    });
    return {
        rows: buildManualRecognitionRows({
            components: template.components,
            amount: options.amount,
            termStart: options.termStart,
            specificDate: options.specificDate,
        }).map((row) => {
            const component = componentsById.get(row.id);
            const sourceDateOffsetDays = toSourceDateOffsetDays({
                component,
                termStart: options.termStart,
                sourceDate: options.sourceDate,
            });
            return {
                key: row.id,
                method: row.method,
                amount: row.amount,
                offsetDays: sourceDateOffsetDays ?? component?.offsetDays ?? 0,
                date:
                    sourceDateOffsetDays === null
                        ? row.date
                        : offsetDate(options.termStart, sourceDateOffsetDays),
                isSpecificDate:
                    component?.anchor === RecognitionAnchor.specificDate,
            };
        }),
        termStart: options.termStart,
        specificDate: options.specificDate,
        termLength: options.term?.length ?? template.termLength,
        termUnit: options.term?.unit ?? template.termUnit,
        holdingAccountId: accountIds?.holdingAccountId ?? null,
        recognitionAccountId:
            options.sourceRecognitionAccountId ??
            accountIds?.recognitionAccountId ??
            null,
    };
}

/**
 * A point-in-time row on "Source date" lands on the source itself, which an
 * accrued term start runs back from. The backend keeps it there unless rows
 * are sent, so the draft keeps it there too, as an offset from the term start.
 */
function toSourceDateOffsetDays(options: {
    component:
        | Pick<
              DraftTemplate["components"][number],
              "method" | "anchor" | "offsetDays"
          >
        | undefined;
    termStart: string;
    sourceDate: string | null | undefined;
}): number | null {
    const { component, termStart, sourceDate } = options;
    if (
        !component ||
        !sourceDate ||
        !termStart ||
        component.method !== RecognitionMethod.pointInTime ||
        !isSourceDocumentAnchor(component.anchor)
    ) {
        return null;
    }
    return daysBetween(termStart, sourceDate) + component.offsetDays;
}

export function daysBetween(from: string, to: string): number {
    if (!from || !to) {
        return 0;
    }
    return differenceInCalendarDays(parseISO(to), parseISO(from));
}

/** A row still needs the specific date the user has not entered yet. */
export function isAwaitingSpecificDate(draft: ScheduleDraft | null): boolean {
    return (
        draft !== null &&
        draft.rows.some((row) => row.isSpecificDate) &&
        draft.specificDate === ""
    );
}

/** Rows back into policy components, each keeping its offset from its anchor. */
export function resolveScheduleDraft(
    draft: ScheduleDraft,
): ResolvedScheduleDraft {
    const termStart = draft.rows.some((row) => !row.isSpecificDate)
        ? draft.termStart
        : null;
    const specificDate = draft.rows.some((row) => row.isSpecificDate)
        ? draft.specificDate
        : "";
    const allocationPercents = allocationPercentsFromAmounts(
        draft.rows.map((row) => row.amount),
    );
    return {
        amount: roundToCents(
            draft.rows.reduce((sum, row) => sum + row.amount, 0),
        ),
        termStart,
        specificDate,
        components: draft.rows.map((row, index) => ({
            method: row.method,
            anchor: row.isSpecificDate
                ? RecognitionAnchor.specificDate
                : RecognitionAnchor.termStart,
            offsetDays: row.offsetDays,
            allocationPercent: allocationPercents[index],
        })),
    };
}

function toComponentKey(component: ScheduleRecognitionComponentInput): string {
    return [
        component.method,
        component.anchor,
        component.offsetDays,
        component.allocationPercent,
    ].join("|");
}

/**
 * Only what differs from the schedule's template run at the draft's own
 * total and dates, so typing an amount or moving the start is never an
 * override; a new split, row, method or account is.
 */
export function buildScheduleDraftOverrides(options: {
    draft: ScheduleDraft;
    template: DraftTemplate;
    entityId: number | null;
    sourceDate?: string | null;
    /** The default the draft started on, so keeping it is not an override. */
    sourceRecognitionAccountId?: string | null;
    /** The term the schedule runs on today: the template's or its own. */
    term: { length: number; unit: ScheduleTermUnit };
}): ScheduleRecognitionOverrides {
    const {
        draft,
        template,
        entityId,
        sourceDate,
        sourceRecognitionAccountId,
        term,
    } = options;
    const resolved = resolveScheduleDraft(draft);
    const reference = createScheduleDraft({
        template,
        entityId,
        amount: resolved.amount,
        termStart: draft.termStart,
        specificDate: draft.specificDate,
        sourceDate,
        sourceRecognitionAccountId,
        term,
    });
    const referenceKeys =
        resolveScheduleDraft(reference).components.map(toComponentKey);
    const hasRowChanges =
        resolved.components.length !== referenceKeys.length ||
        resolved.components.some(
            (component, index) =>
                toComponentKey(component) !== referenceKeys[index],
        );
    const hasTermChange =
        draft.termLength !== term.length || draft.termUnit !== term.unit;
    return {
        ...(hasRowChanges ? { components: resolved.components } : {}),
        ...(draft.holdingAccountId !== reference.holdingAccountId &&
        draft.holdingAccountId
            ? { holdingAccountId: draft.holdingAccountId }
            : {}),
        ...(draft.recognitionAccountId !== reference.recognitionAccountId &&
        draft.recognitionAccountId
            ? { recognitionAccountId: draft.recognitionAccountId }
            : {}),
        ...(hasTermChange
            ? { termLength: draft.termLength, termUnit: draft.termUnit }
            : {}),
    };
}

/** Why the draft cannot be applied yet, in the words the modal shows. */
export function getScheduleDraftError(options: {
    draft: ScheduleDraft;
    /** A source-backed schedule must spread exactly the source amount. */
    fixedAmount?: number;
}): string | null {
    const { draft, fixedAmount } = options;
    if (draft.rows.length === 0) {
        return "Add a recognition row";
    }
    if (draft.rows.some((row) => row.amount <= 0)) {
        return "Enter an amount for each row";
    }
    if (draft.rows.some((row) => !row.date)) {
        return "Pick a date for each row";
    }
    const termRowCount = draft.rows.filter((row) =>
        isTermRecognitionMethod(row.method),
    ).length;
    if (termRowCount > 1) {
        return SINGLE_TERM_ROW_MESSAGE;
    }
    if (termRowCount === 1 && draft.termLength < 1) {
        return "Enter a term of at least 1";
    }
    if (
        !isDailyProRataTermUnitAllowed({
            components: draft.rows,
            termUnit: draft.termUnit,
        })
    ) {
        return DAILY_PRO_RATA_TERM_UNITS_MESSAGE;
    }
    const resolved = resolveScheduleDraft(draft);
    if (fixedAmount !== undefined && resolved.amount !== fixedAmount) {
        return `Rows must add up to ${currencyFormatter.format(fixedAmount)}`;
    }
    if (
        resolved.components.some(
            (component) => component.allocationPercent <= 0,
        )
    ) {
        return "Each row needs a larger share of the total";
    }
    const postedAmounts = splitAmountByAllocationPercents(
        resolved.amount,
        resolved.components.map((component) => component.allocationPercent),
    );
    if (
        postedAmounts.some(
            (amount, index) =>
                amount !== roundToCents(draft.rows[index].amount),
        )
    ) {
        return `Rows split the total in 0.01% steps, so these post as ${postedAmounts
            .map((amount) => currencyFormatter.format(amount))
            .join(", ")}. Enter those amounts instead.`;
    }
    return null;
}
